import {inject, NgModule} from '@angular/core';
import {Router, RouterModule, Routes} from '@angular/router';
import {AuthService} from './auth/services/auth.service';
import {UserContextService} from './services/user-context.service';
import {PersonalInformationComponent} from './components/personal-information/personal-information.component';
import {SearchMemberComponent} from './components/search-member/search-member.component';
import {ContributionComponent} from './components/contribution/contribution.component';
import {AllowancesComponent} from './components/allowances/allowances.component';
import {AllowancesOverviewComponent} from './components/allowances/allowances-overview/allowances-overview.component';
import {TaxFormComponent} from './components/tax-form/tax-form.component';
import {MemberIsPresentGuard} from './guards/member-is-present.guard';
import {AbvvMembershipComponent} from './components/abvv-membership/abvv-membership.component';
import {UnemploymentComponent} from './components/unemployment/unemployment.component';
import {AttestationsComponent} from './components/attestations/attestations.component';
import {RequestsComponent} from './components/requests/requests.component';
import {VacationComponent} from './components/vacation/vacation.component';
import {PermitsComponent} from './components/permits/permits.component';
import {EDocsComponent} from './components/e-docs/e-docs.component';
import {EDocsDetailComponent} from './components/e-docs/e-docs-detail/e-docs-detail.component';
import {EDocsOverviewComponent} from './components/e-docs/e-docs-overview/e-docs-overview.component';
import {
  AllowancesDetailByPaymentDateComponent
} from './components/allowances/allowances-detail/allowances-detail-by-payment-date.component';
import {
  AllowancesDetailByUnemploymentMonthComponent
} from './components/allowances/allowances-detail/allowances-detail-by-unemployment-month.component';
import {MaintenanceComponent} from './components/maintenance/maintenance.component';
import {RecentInfosComponent} from './components/recent-infos/recent-infos.component';
import {AskQuestionComponent} from './components/ask-question/ask-question.component';
import {InboxComponent} from './components/inbox/inbox.component';
import {MobilePlusComponent} from './navigation/mobile-plus/mobile-plus.component';
import {MobileOnlyGuard} from './guards/mobile-only.guard';

const myAbvvRoutes: Routes = [
  {
    path: 'membership', component: AbvvMembershipComponent, canActivate: [MemberIsPresentGuard],
    children: [
      {path: '', pathMatch: 'full', redirectTo: 'personal-data'},
      {path: 'personal-data', component: PersonalInformationComponent},
      {path: 'contributions', component: ContributionComponent}
    ]
  },
  {
    path: 'unemployment', component: UnemploymentComponent, canActivate: [MemberIsPresentGuard],
    children: [
      {path: '', pathMatch: 'full', redirectTo: 'recent-infos'},
      {path: 'recent-infos', component: RecentInfosComponent},
      {path: 'inbox', component: InboxComponent},
      {
        path: 'payments', component: AllowancesComponent,
        children: [
          {path: '', pathMatch: 'full', redirectTo: 'overview'},
          {path: 'overview', component: AllowancesOverviewComponent},
          {path: 'detailByUnemploymentMonth', component: AllowancesDetailByUnemploymentMonthComponent},
          {path: 'detailByPaymentDate', component: AllowancesDetailByPaymentDateComponent},
        ]
      },
      {path: 'tax-forms', component: TaxFormComponent},
      {path: 'e-docs', component: EDocsComponent,
        children: [
          {path: '', pathMatch: 'full', redirectTo: 'overview'},
          {path: 'overview', component: EDocsOverviewComponent},
          {path: 'detail', component: EDocsDetailComponent}
        ]},
      {path: 'attestations', component: AttestationsComponent},
      {path: 'requests', component: RequestsComponent},
      {path: 'vacation', component: VacationComponent},
      {path: 'permits', component: PermitsComponent},
    ]
  },
  {path: 'menu'        , component: MobilePlusComponent   , canActivate: [MobileOnlyGuard, MemberIsPresentGuard]},
  {path: 'search'      , component: SearchMemberComponent, canActivate: [MemberIsPresentGuard]},
  {path: 'ask-question', component: AskQuestionComponent , canActivate: [MemberIsPresentGuard] , runGuardsAndResolvers: 'always'},
  {path: 'maintenance' , component: MaintenanceComponent},
  {path: 'auth'        , loadChildren: () => import('./auth/auth.module').then(m => m.AuthModule)},
  // The iOS OAuth redirect URI is the Universal Link https://my.abvv.be/oauth-callback
  // (see PlatformService.getRedirectUri). When iOS honours the association it opens the
  // app and DeepLinkService routes the callback. When it does NOT — a stale or
  // mis-declared apple-app-site-association, or a device that has not fetched it yet —
  // Safari stays on the URL and the web app is served instead, where /oauth-callback
  // matched nothing and the wildcard rendered "page not found" with the authorization
  // code sitting in the URL bar.
  //
  // Declaring the path means the login completes in the browser in that case rather than
  // dead-ending. queryParams are forwarded explicitly: the code and state live there.
  {
    path: 'oauth-callback',
    pathMatch: 'full',
    redirectTo: ({ queryParams }) => inject(Router).createUrlTree(['/auth/callback'], { queryParams }),
  },
  // Default landing: the post-login URL '/' resolves differently per user type
  // (see CLAUDE.md "Dual User Types"). Previously hard-coded to 'search', which
  // forced external/DMZ users through /search where MemberIsPresentGuard had to
  // fire-and-forget-redirect them to /membership — a race that occasionally
  // landed on the wildcard 404. Function-based redirectTo (Angular 18+) resolves
  // synchronously to a UrlTree, eliminating the race. (MAF-503)
  {
    path: '',
    pathMatch: 'full',
    redirectTo: () => {
      const router = inject(Router);
      const authService = inject(AuthService);
      const userContext = inject(UserContextService);

      if (!authService.isAuthenticated()) {
        return router.parseUrl('/auth/landing');
      }
      return userContext.isInternalUser()
        ? router.parseUrl('/search')
        : router.parseUrl('/membership');
    },
  },
  {path: '**'          , loadComponent: () => import('./components/not-found/not-found.component').then(m => m.NotFoundComponent)},
];

@NgModule({
  imports: [
    RouterModule.forRoot(
      myAbvvRoutes,
      {
        enableTracing: false
      }
    )
  ],
  exports: [RouterModule]
})
export class AppRoutingModule {
}
